
import React, { useState, useEffect } from 'react';
import { uploadAPI } from '../../services/api.js';
import toast from 'react-hot-toast';

const PromotionForm = ({ promotion, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'banner',
    status: 'inactive',
    bannerImage: { url: '', alt: '' },
    backgroundColor: '#ffffff',
    textColor: '#333333',
    discountType: 'percentage',
    discountValue: 0,
    targetType: 'all_products',
    targetCategories: [],
    targetProducts: [],
    startDate: '',
    endDate: '',
    position: 'top_banner',
    priority: 1,
    isFeatured: false,
    ctaText: 'Shop Now',
    ctaLink: '/products',
    minimumOrderAmount: 0,
    usageLimit: 0,
  });
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories and products for targeting
  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await fetch('http://localhost:5000/api/categories');
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData.data || categoriesData || []);

        const productsResponse = await fetch('http://localhost:5000/api/admin/products');
        const productsData = await productsResponse.json();
        setProducts(productsData.products || productsData.data || productsData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  // Populate form if editing
  useEffect(() => {
    if (promotion) {
      // Format dates for input fields
      const formatDateForInput = (dateString) => {
        return new Date(dateString).toISOString().split('T')[0];
      };

      setFormData({
        title: promotion.title || '',
        description: promotion.description || '',
        type: promotion.type || 'banner',
        status: promotion.status || 'inactive',
        bannerImage: promotion.bannerImage || { url: '', alt: '' },
        backgroundColor: promotion.backgroundColor || '#ffffff',
        textColor: promotion.textColor || '#333333',
        discountType: promotion.discountType || 'percentage',
        discountValue: promotion.discountValue || 0,
        targetType: promotion.targetType || 'all_products',
        targetCategories: promotion.targetCategories || [],
        targetProducts: promotion.targetProducts || [],
        startDate: promotion.startDate ? formatDateForInput(promotion.startDate) : '',
        endDate: promotion.endDate ? formatDateForInput(promotion.endDate) : '',
        position: promotion.position || 'top_banner',
        priority: promotion.priority || 1,
        isFeatured: promotion.isFeatured || false,
        ctaText: promotion.ctaText || 'Shop Now',
        ctaLink: promotion.ctaLink || '/products',
        minimumOrderAmount: promotion.minimumOrderAmount || 0,
        usageLimit: promotion.usageLimit || 0,
      });
    } else {
      // Set default start date to today and end date to 7 days from now
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      setFormData(prev => ({
        ...prev,
        startDate: today,
        endDate: nextWeek,
      }));
    }
  }, [promotion]);

  // Image upload handler
  const handleImageUpload = async (file) => {
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await uploadAPI.uploadSingle(uploadFormData);
      const imageData = {
        url: response.data?.url || response.url,
        alt: file.name || 'Promotion banner'
      };

      setFormData(prev => ({
        ...prev,
        bannerImage: imageData
      }));
      
      toast.success('Banner image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (name, value, isChecked) => {
    setFormData(prev => ({
      ...prev,
      [name]: isChecked 
        ? [...prev[name], value]
        : prev[name].filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate dates
      if (new Date(formData.startDate) >= new Date(formData.endDate)) {
        toast.error('End date must be after start date');
        setLoading(false);
        return;
      }

      // Validate discount value
      if (formData.discountValue < 0) {
        toast.error('Discount value cannot be negative');
        setLoading(false);
        return;
      }

      // Prepare data for API
      const submitData = {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        priority: parseInt(formData.priority),
        minimumOrderAmount: parseFloat(formData.minimumOrderAmount),
        usageLimit: parseInt(formData.usageLimit),
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
      };

      const url = promotion 
        ? `http://localhost:5000/api/promotions/${promotion._id}`
        : 'http://localhost:5000/api/promotions';
      
      const method = promotion ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) throw new Error('Failed to save promotion');

      const result = await response.json();
      
      toast.success(promotion ? 'Promotion updated successfully!' : 'Promotion created successfully!');
      onSave();
      
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast.error(`Failed to ${promotion ? 'update' : 'create'} promotion`);
    } finally {
      setLoading(false);
    }
  };

  // Preview styles based on form data
  const previewStyle = {
    backgroundColor: formData.backgroundColor,
    color: formData.textColor,
    padding: isSmallScreen ? '12px' : (isMobile ? '16px' : '20px'),
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: isSmallScreen ? '12px' : (isMobile ? '16px' : '20px'),
    border: '2px dashed #ddd',
    minHeight: isSmallScreen ? '80px' : '100px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  };

  // Responsive modal styles
  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
  };

  const contentStyle = {
    width: '100%',
    maxWidth: isSmallScreen ? '95vw' : (isMobile ? '90vw' : '900px'),
    maxHeight: isSmallScreen ? '85vh' : '90vh',
    overflowY: 'auto',
    borderRadius: isSmallScreen ? 'var(--radius-md)' : 'var(--radius-lg)'
  };

  return (
    <div style={modalStyle}>
      <div className="card" style={contentStyle}>
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4)'),
          position: 'sticky',
          top: 0,
          backgroundColor: 'var(--bg-white)',
          zIndex: 10
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flex: 1
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>
                {promotion ? '✏️' : '➕'}
              </span>
              {isSmallScreen ? (promotion ? 'Edit' : 'New') : (promotion ? 'Edit Promotion' : 'Create New Promotion')}
            </h3>
            
            <button 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: isSmallScreen ? '1.3rem' : '1.5rem',
                cursor: 'pointer',
                color: 'var(--text-light)',
                padding: isSmallScreen ? '4px' : '8px',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)') }}>
              
              {/* Basic Information */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Basic Information
                </h4>
                <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)') }}>
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Promotion Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter promotion title"
                      required
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="form-textarea"
                      placeholder="Enter promotion description"
                      rows={isSmallScreen ? 2 : 3}
                      required
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
                    gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                  }}>
                    <div className="form-group">
                      <label className="form-label" style={{
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        marginBottom: '4px',
                        display: 'block'
                      }}>
                        Promotion Type *
                      </label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                        style={{
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      >
                        <option value="banner">Banner</option>
                        <option value="flash_sale">Flash Sale</option>
                        <option value="seasonal">Seasonal</option>
                        <option value="special_offer">Special Offer</option>
                        <option value="black_friday">Black Friday</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        marginBottom: '4px',
                        display: 'block'
                      }}>
                        Display Position *
                      </label>
                      <select
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                        style={{
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      >
                        <option value="top_banner">Top Banner</option>
                        <option value="hero">Hero Section</option>
                        <option value="sidebar">Sidebar</option>
                        <option value="popup">Popup</option>
                        <option value="product_page">Product Page</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
                    gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                  }}>
                    <div className="form-group">
                      <label className="form-label" style={{
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        marginBottom: '4px',
                        display: 'block'
                      }}>
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="form-select"
                        style={{
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      >
                        <option value="inactive">Inactive</option>
                        <option value="active">Active</option>
                        <option value="scheduled">Scheduled</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        marginBottom: '4px',
                        display: 'block'
                      }}>
                        Priority (1-10)
                      </label>
                      <input
                        type="number"
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="form-input"
                        min="1"
                        max="10"
                        required
                        style={{
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 'var(--space-2)', 
                      cursor: 'pointer',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}>
                      <input
                        type="checkbox"
                        name="isFeatured"
                        checked={formData.isFeatured}
                        onChange={handleInputChange}
                        style={{
                          width: isSmallScreen ? '16px' : '18px',
                          height: isSmallScreen ? '16px' : '18px'
                        }}
                      />
                      <span>Featured Promotion (will be highlighted)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Banner Image - MOBILE FRIENDLY */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Banner Image
                </h4>
                
                <div className="form-group">
                  <div style={{ 
                    border: '2px dashed var(--border-medium)', 
                    borderRadius: 'var(--radius-md)',
                    padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-light)',
                    transition: 'all 0.2s ease',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.6 : 1,
                    minHeight: isSmallScreen ? '120px' : '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  onClick={() => !formData.bannerImage.url && !uploading && document.getElementById('bannerImage').click()}
                  >
                    {formData.bannerImage.url ? (
                      <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                        <img 
                          src={formData.bannerImage.url} 
                          alt="Banner preview" 
                          style={{
                            maxWidth: '100%',
                            maxHeight: isSmallScreen ? '120px' : (isMobile ? '150px' : '200px'),
                            borderRadius: 'var(--radius-md)',
                            border: '2px solid var(--accent-gold)',
                            objectFit: 'contain'
                          }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, bannerImage: { url: '', alt: '' } }));
                          }}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: 'var(--error)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: isSmallScreen ? '24px' : '28px',
                            height: isSmallScreen ? '24px' : '28px',
                            cursor: 'pointer',
                            fontSize: isSmallScreen ? '12px' : '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ 
                          fontSize: isSmallScreen ? '2rem' : (isMobile ? '2.5rem' : '3rem'), 
                          marginBottom: isSmallScreen ? '8px' : 'var(--space-3)',
                          color: 'var(--text-light)'
                        }}>
                          🖼️
                        </div>
                        <p style={{ 
                          marginBottom: isSmallScreen ? '8px' : 'var(--space-3)', 
                          color: 'var(--text-light)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {uploading ? 'Uploading...' : 'Click to upload banner image'}
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleImageUpload(e.target.files[0]);
                            }
                          }}
                          style={{ display: 'none' }}
                          id="bannerImage"
                          disabled={uploading}
                        />
                        <label 
                          htmlFor="bannerImage"
                          className="btn btn-gold"
                          style={{ 
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            padding: isSmallScreen ? '6px 12px' : '8px 16px',
                            fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {uploading ? 'Uploading...' : 'Choose Banner Image'}
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Design Settings */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Design Settings
                </h4>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
                  gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                }}>
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Background Color
                    </label>
                    <div style={{ display: 'flex', gap: isSmallScreen ? '8px' : 'var(--space-2)', alignItems: 'center' }}>
                      <input
                        type="color"
                        name="backgroundColor"
                        value={formData.backgroundColor}
                        onChange={handleInputChange}
                        style={{
                          width: isSmallScreen ? '40px' : '50px',
                          height: isSmallScreen ? '30px' : '40px',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      />
                      <input
                        type="text"
                        name="backgroundColor"
                        value={formData.backgroundColor}
                        onChange={handleInputChange}
                        className="form-input"
                        style={{ 
                          flex: 1,
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Text Color
                    </label>
                    <div style={{ display: 'flex', gap: isSmallScreen ? '8px' : 'var(--space-2)', alignItems: 'center' }}>
                      <input
                        type="color"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleInputChange}
                        style={{
                          width: isSmallScreen ? '40px' : '50px',
                          height: isSmallScreen ? '30px' : '40px',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      />
                      <input
                        type="text"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleInputChange}
                        className="form-input"
                        style={{ 
                          flex: 1,
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div style={{ marginTop: isSmallScreen ? '12px' : (isMobile ? '16px' : 'var(--space-4)') }}>
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Preview
                  </label>
                  <div style={previewStyle}>
                    {formData.bannerImage.url ? (
                      <img 
                        src={formData.bannerImage.url} 
                        alt="Banner preview" 
                        style={{
                          maxWidth: '100%',
                          maxHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
                          borderRadius: 'var(--radius-md)',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <>
                        <h3 style={{ 
                          margin: '0 0 8px 0', 
                          color: formData.textColor,
                          fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)'
                        }}>
                          {formData.title || 'Promotion Title'}
                        </h3>
                        <p style={{ 
                          margin: 0, 
                          color: formData.textColor,
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {formData.description || 'Promotion description will appear here'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Discount Settings */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Discount Settings
                </h4>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
                  gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                }}>
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Discount Type
                    </label>
                    <select
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleInputChange}
                      className="form-select"
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    >
                      <option value="percentage">Percentage Off</option>
                      <option value="fixed">Fixed Amount Off</option>
                      <option value="free_shipping">Free Shipping</option>
                      <option value="bogo">Buy One Get One</option>
                    </select>
                  </div>

                  {(formData.discountType === 'percentage' || formData.discountType === 'fixed') && (
                    <div className="form-group">
                      <label className="form-label" style={{
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        marginBottom: '4px',
                        display: 'block'
                      }}>
                        {formData.discountType === 'percentage' ? 'Discount %' : 'Amount (KSh)'}
                      </label>
                      <input
                        type="number"
                        name="discountValue"
                        value={formData.discountValue}
                        onChange={handleInputChange}
                        className="form-input"
                        min="0"
                        max={formData.discountType === 'percentage' ? '100' : undefined}
                        step={formData.discountType === 'percentage' ? '1' : '0.01'}
                        style={{
                          padding: isSmallScreen ? '8px 12px' : '10px 16px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Targeting */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Targeting
                </h4>
                
                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Target Type
                  </label>
                  <select
                    name="targetType"
                    value={formData.targetType}
                    onChange={handleInputChange}
                    className="form-select"
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  >
                    <option value="all_products">All Products</option>
                    <option value="specific_categories">Specific Categories</option>
                    <option value="specific_products">Specific Products</option>
                    <option value="collection">Collection</option>
                  </select>
                </div>

                {formData.targetType === 'specific_categories' && (
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Select Categories
                    </label>
                    <div style={{ 
                      maxHeight: isSmallScreen ? '150px' : '200px', 
                      overflowY: 'auto', 
                      border: '1px solid var(--border-light)', 
                      borderRadius: 'var(--radius-md)', 
                      padding: isSmallScreen ? '12px' : 'var(--space-3)',
                      backgroundColor: 'var(--bg-light)'
                    }}>
                      {categories.map(category => (
                        <label key={category._id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 'var(--space-2)', 
                          marginBottom: isSmallScreen ? '8px' : 'var(--space-2)', 
                          cursor: 'pointer',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.targetCategories.includes(category._id)}
                            onChange={(e) => handleArrayChange('targetCategories', category._id, e.target.checked)}
                            style={{
                              width: isSmallScreen ? '16px' : '18px',
                              height: isSmallScreen ? '16px' : '18px'
                            }}
                          />
                          <span>{category.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {formData.targetType === 'specific_products' && (
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Select Products
                    </label>
                    <div style={{ 
                      maxHeight: isSmallScreen ? '150px' : '200px', 
                      overflowY: 'auto', 
                      border: '1px solid var(--border-light)', 
                      borderRadius: 'var(--radius-md)', 
                      padding: isSmallScreen ? '12px' : 'var(--space-3)',
                      backgroundColor: 'var(--bg-light)'
                    }}>
                      {products.map(product => (
                        <label key={product._id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 'var(--space-2)', 
                          marginBottom: isSmallScreen ? '8px' : 'var(--space-2)', 
                          cursor: 'pointer',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.targetProducts.includes(product._id)}
                            onChange={(e) => handleArrayChange('targetProducts', product._id, e.target.checked)}
                            style={{
                              width: isSmallScreen ? '16px' : '18px',
                              height: isSmallScreen ? '16px' : '18px',
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: '500' }}>{product.name}</span>
                            <div style={{ 
                              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-xs)', 
                              color: 'var(--text-light)',
                              display: 'flex',
                              gap: 'var(--space-2)',
                              flexWrap: 'wrap'
                            }}>
                              <span>KSh {product.price?.toLocaleString() || 'N/A'}</span>
                              {product.stock && <span>• Stock: {product.stock}</span>}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Call to Action */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Call to Action
                </h4>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
                  gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                }}>
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Button Text
                    </label>
                    <input
                      type="text"
                      name="ctaText"
                      value={formData.ctaText}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., Shop Now, Learn More"
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Link/URL
                    </label>
                    <input
                      type="text"
                      name="ctaLink"
                      value={formData.ctaLink}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="/products, /offers, https://..."
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Schedule & Limits */}
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Schedule & Limits
                </h4>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr 1fr' : '1fr 1fr 1fr'),
                  gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                }}>
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      End Date *
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Minimum Order (KSh)
                    </label>
                    <input
                      type="number"
                      name="minimumOrderAmount"
                      value={formData.minimumOrderAmount}
                      onChange={handleInputChange}
                      className="form-input"
                      min="0"
                      step="0.01"
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr 1fr' : '1fr 1fr 1fr'),
                  gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  marginTop: isSmallScreen ? '12px' : (isMobile ? '16px' : 'var(--space-4)')
                }}>
                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Usage Limit
                    </label>
                    <input
                      type="number"
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleInputChange}
                      className="form-input"
                      min="0"
                      placeholder="0 = unlimited"
                      style={{
                        padding: isSmallScreen ? '8px 12px' : '10px 16px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Current Status
                    </label>
                    <div style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      backgroundColor: formData.status === 'active' ? 'var(--success-light)' : 
                                     formData.status === 'scheduled' ? 'var(--warning-light)' : 'var(--error-light)',
                      color: formData.status === 'active' ? 'var(--success)' : 
                            formData.status === 'scheduled' ? 'var(--warning)' : 'var(--error)',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: '500',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      textTransform: 'capitalize',
                      border: `1px solid ${formData.status === 'active' ? 'var(--success-border)' : 
                              formData.status === 'scheduled' ? 'var(--warning-border)' : 'var(--error-border)'}`
                    }}>
                      {formData.status}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Form Actions */}
          <div className="card-footer" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4)'),
            borderTop: '1px solid var(--border-light)',
            backgroundColor: 'var(--bg-lighter)',
            position: 'sticky',
            bottom: 0,
            zIndex: 10
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
              flexWrap: isSmallScreen ? 'wrap' : 'nowrap'
            }}>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
                style={{
                  padding: isSmallScreen ? '8px 16px' : '10px 20px',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                  flex: isSmallScreen ? '1' : 'none'
                }}
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={loading || uploading}
                className="btn btn-gold"
                style={{
                  padding: isSmallScreen ? '8px 16px' : '10px 20px',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                  opacity: (loading || uploading) ? 0.7 : 1,
                  cursor: (loading || uploading) ? 'not-allowed' : 'pointer',
                  flex: isSmallScreen ? '1' : 'none'
                }}
              >
                {loading ? (
                  <>
                    <span style={{ 
                      display: 'inline-block', 
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}>
                      ⏳
                    </span>
                    {promotion ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <span style={{ marginRight: '8px' }}>
                      {promotion ? '💾' : '✨'}
                    </span>
                    {promotion ? 'Update Promotion' : 'Create Promotion'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .card {
          background-color: var(--bg-white);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .form-input, .form-select, .form-textarea {
          width: 100%;
          border: 1px solid var(--border-medium);
          border-radius: var(--radius-md);
          background-color: var(--bg-white);
          color: var(--text-dark);
          transition: all 0.2s ease;
          font-family: inherit;
        }
        
        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: var(--accent-gold);
          box-shadow: 0 0 0 3px var(--accent-gold-light);
        }
        
        .form-input:hover, .form-select:hover, .form-textarea:hover {
          border-color: var(--border-dark);
        }
        
        .form-label {
          color: var(--text-dark);
          font-weight: 500;
          display: block;
        }
        
        .btn {
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
        }
        
        .btn-gold {
          background-color: var(--accent-gold);
          color: white;
        }
        
        .btn-gold:hover:not(:disabled) {
          background-color: var(--accent-gold-dark);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }
        
        .btn-outline {
          background-color: transparent;
          color: var(--text-dark);
          border: 1px solid var(--border-medium);
        }
        
        .btn-outline:hover {
          background-color: var(--bg-light);
          border-color: var(--border-dark);
        }
      `}</style>
    </div>
  );
};

export default PromotionForm;